import { Col, DatePicker, Form, Result, Row, Spin, Table, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { CommonForm } from '../Shared/CommonForm';
import { FormFooter } from '../Shared/FormFooter';
import { FormContainer, FormItemInputStyled, FormItemStyled, PageTitle, PrimaryButton } from '../Styled/GlobalStyles';
import BillOfLadingInput from './BillOfLadingInput';
import moment from 'moment';
import { ErrorResponseDto } from '../../models/ErrorResponseDto';
import { showNotification } from '../../util/Notifications';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { logEvent } from '../../util/Analytics';
import { logUserTrack } from '../Shared/userTrack';

const FreightRelease = () => {
  const [form] = Form.useForm();
  const [freightRelease, setFreightRelease] = useState<any>(null);
  const [freightReleaseError, setFreightReleaseError] = useState<ErrorResponseDto | null>(null);

  const [isFreightReleaseLoading, setIsFreightReleaseLoading] = useState<boolean>(false);
  const [isFormDisabled, setIsFormDisabled] = useState<boolean>(true);

  const billOfLadingsValue = Form.useWatch('billOfLadings', form);
  const releaseDateValue = Form.useWatch('releaseDate', form);
  const passwordValue = Form.useWatch('password', form);

  useEffect(() => {
    if (!billOfLadingsValue || !releaseDateValue || !passwordValue) {
      setIsFormDisabled(true);
      return;
    } else if (billOfLadingsValue.length && billOfLadingsValue[0].length < 5) {
      setIsFormDisabled(true);
      return;
    } else if (billOfLadingsValue.length !== 0) {
      setIsFormDisabled(false);
      return;
    }
    setIsFormDisabled(true);
  }, [billOfLadingsValue, releaseDateValue, passwordValue]);

  const onFormSubmit = (values: any) => {
    logEvent('freight_release_submit');

    if (!values.billOfLadings || !values.releaseDate || !values.password) {
      console.log('Form Submitted with invalid values!');
      return;
    }

    try {
      logUserTrack('click', 'submit_freight_release_form', values);
    } catch (error) {
      console.log(error);
    }

    values['releaseDate'] = values['releaseDate'].format('MM/DD/YYYY');
    setIsFreightReleaseLoading(true);
    fetch(`/api/freightRelease`, {
      method: 'post',
      body: JSON.stringify(values),
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (!data.status) {
          setFreightRelease(data);
        } else {
          setFreightReleaseError(data);
          showNotification({
            type: 'error',
            title: 'Error',
            message: data.message,
          });
        }
      })
      .finally(() => setIsFreightReleaseLoading(false));
  };

  const resetForm = () => {
    setFreightReleaseError(null);
    setFreightRelease(null);
    form.resetFields();
  };

  const userClickedClearForm = () => {
    logEvent('freight_release_clear');
    resetForm();
  };

  const userClickedSearchAgain = () => {
    logEvent('freight_release_search_again');
    resetForm();
  };

  const userClickedGoBack = () => {
    logEvent('freight_release_search_again');
    resetForm();
  };

  const onReleaseDateChange = (date: any, dateString: any) => {
    form.setFieldValue('releaseDate', date);
  };

  useEffect(() => {
    document.title = 'Freight Release';
  }, []);

  const onBillOfLadingChange = (value: any) => {
    form.setFieldValue('billOfLadings', value);
  };

  const disabledDate = (currentDate: any): boolean => {
    return currentDate ? currentDate.isBefore(moment().startOf('day')) : false;
  };

  const successResponseTableColumns = [
    {
      title: 'Bill Of Lading',
      dataIndex: 'billOfLading',
    },
    {
      title: 'Container Number',
      dataIndex: 'containerNumber',
    },
  ];

  return (
    <>
      <PageTitle>Freight Release</PageTitle>
      {freightReleaseError && (
        <FormContainer>
          <Result
            status='error'
            title='Error'
            subTitle={freightReleaseError.message}
            icon={<QuestionCircleOutlined />}
            extra={[
              <PrimaryButton type='primary' key='console' onClick={userClickedSearchAgain}>
                Search Again
              </PrimaryButton>,
            ]}
          ></Result>
        </FormContainer>
      )}
      {freightRelease && (
        <>
          <FormContainer>
            <Result
              status='success'
              title='Freight Release Successful'
              subTitle={
                <>
                  <Typography.Text>Release Scheduled For {freightRelease.releaseDate}</Typography.Text>
                  <Row justify={'center'} style={{ marginTop: 20 }}>
                    <Col span={12}>
                      <Table
                        bordered={true}
                        columns={successResponseTableColumns}
                        dataSource={freightRelease.containers}
                        rowKey={'containerNumber'}
                        pagination={false}
                      />
                    </Col>
                  </Row>
                </>
              }
              extra={[
                <PrimaryButton type='primary' key='console' onClick={userClickedGoBack}>
                  Go Back
                </PrimaryButton>,
              ]}
            />
          </FormContainer>
        </>
      )}
      {!freightRelease && !freightReleaseError && (
        <CommonForm form={form} onFinish={onFormSubmit}>
          <Spin spinning={isFreightReleaseLoading} size='large'>
            <FormContainer>
              <FormItemStyled name='billOfLadings' label='Bill Of Lading'>
                <BillOfLadingInput onChange={onBillOfLadingChange} />
              </FormItemStyled>
              <FormItemStyled name='releaseDate' label='Release Date'>
                <DatePicker
                  onChange={onReleaseDateChange}
                  mode={'date'}
                  disabledDate={disabledDate}
                  format={'MM/DD/YYYY'}
                />
              </FormItemStyled>
              <FormItemStyled name='password' label='Password'>
                <FormItemInputStyled type='password' size='large' />
              </FormItemStyled>
            </FormContainer>
            <FormFooter searchDisabled={isFormDisabled} submitText={'Submit'} resetForm={userClickedClearForm} />
          </Spin>
        </CommonForm>
      )}
    </>
  );
};

export default FreightRelease;
